import React, { useState, useEffect } from "react";
import {
  Edit2,
  Save,
  X,
  Trash2,
  TrendingUp,
  CheckCircle,
  XCircle,
  Loader2,
  Sparkles,
  RotateCcw,
} from "lucide-react";
import axios from "axios";
import { apiUrl } from "../../constants";
import { toast } from "react-toastify";
import coin from "../../assets/coin_only.png";

// Delete Modal Component
const DeleteModal = ({ isOpen, onClose, onConfirm, onUndo, activityName, isDeleting, showUndo }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 animate-fade-in">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}></div>
      <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-scale-in">
        {!showUndo ? (
          <>
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
              <Trash2 className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 text-center mb-2">
              Delete Activity?
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete <span className="font-semibold text-gray-800">"{activityName}"</span>? You can't undo this action.
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={onConfirm}
                disabled={isDeleting}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-xl font-semibold hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-5 h-5" />
                    Delete
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 text-center mb-2">
              Activity Deleted
            </h3>
            <p className="text-gray-600 text-center mb-6">
              <span className="font-semibold text-gray-800">"{activityName}"</span> has been deleted successfully.
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-xl font-semibold hover:bg-gray-200 transition-colors"
              >
                Close
              </button>
              <button
                onClick={onUndo}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-semibold hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2"
              >
                <RotateCcw className="w-5 h-5" />
                Undo Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const ActivityCard = ({ activity, onUpdate, onDelete }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(activity.name);
  const [editedCoins, setEditedCoins] = useState(activity.coins_required);
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const handleSave = async () => {
    setLoading(true);
    try {
      await onUpdate(activity._id, {
        name: editedName,
        coins_required: editedCoins,
      });
      setIsEditing(false);
      toast.success("Activity updated successfully! 🎉");
    } catch (error) {
      console.error("Update failed:", error);
      toast.error("Failed to update activity");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditedName(activity.name);
    setEditedCoins(activity.coins_required);
    setIsEditing(false);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await onDelete(activity._id, activity.name);
      setShowDeleteModal(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete activity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-100">
        <div className="bg-gradient-to-r from-blue-500 to-purple-600 p-4">
          <div className="flex items-center justify-between">
            {isEditing ? (
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="bg-white/20 backdrop-blur-sm text-white placeholder-white/70 px-3 py-2 rounded-lg flex-1 mr-2 focus:outline-none focus:ring-2 focus:ring-white/50"
                placeholder="Activity Name"
              />
            ) : (
              <h3 className="text-xl font-bold text-white flex-1">
                {activity.name}
              </h3>
            )}

            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <Edit2 className="w-5 h-5 text-white" />
              </button>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-center gap-2 mb-4 bg-green-50 px-4 py-2 rounded-lg w-fit">
            <TrendingUp className="w-5 h-5 text-green-600" />
            <span className="text-sm font-semibold text-green-700">
              {activity.bookings} Bookings
            </span>
          </div>

          <div className="mb-6">
            <label className="text-sm font-medium text-gray-600 mb-2 block">
              Coins Required
            </label>
            {isEditing ? (
              <input
                type="number"
                value={editedCoins}
                onChange={(e) => setEditedCoins(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                placeholder="Enter coins"
              />
            ) : (
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold text-gray-800">
                  {activity.coins_required}
                </span>
                <img src={coin} className="text-lg text-yellow-500" style={{ width: '25px', height: '25px' }} alt="Coins" />
              </div>
            )}
          </div>

          {isEditing ? (
            <div className="flex gap-3">
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-blue-700 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="px-6 bg-gray-100 text-gray-700 py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <X className="w-5 h-5" />
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={handleDelete}
              disabled={loading}
              className="w-full bg-red-50 text-red-600 py-3 rounded-lg font-semibold hover:bg-red-100 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Trash2 className="w-5 h-5" />
              {loading ? "Deleting..." : "Delete Activity"}
            </button>
          )}
        </div>
      </div>

      <DeleteModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        activityName={activity.name}
        isDeleting={loading}
        showUndo={false}
      />
    </>
  );
};

const ActivityUpdate = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletedActivity, setDeletedActivity] = useState(null);
  const [showUndoModal, setShowUndoModal] = useState(false);

  useEffect(() => {
    fetchActivities();
  }, []);

  const fetchActivities = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${apiUrl}/activity/activityfetch`);
      if (Array.isArray(response.data)) {
        setActivities(response.data);
      } else if (response.data.success && response.data.data) {
        setActivities(response.data.data);
      } else {
        toast.error("Failed to fetch activities");
      }
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Failed to fetch activities", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (id, updateData) => {
    try {
      const response = await axios.put(
        `${apiUrl}/activity/activityupdate/${id}`,
        updateData
      );

      if (response.data.success) {
        setActivities(
          activities.map((activity) =>
            activity._id === id ? { ...activity, ...updateData } : activity
          )
        );
      } else {
        toast.error(
          response.data.message || "Failed to update activity",
          "error"
        );
      }
    } catch (error) {
      console.error("Update error:", error);
      toast.error(
        error.response?.data?.message || "Error updating activity",
        "error"
      );
    }
  };

  const handleDelete = async (id, activityName) => {
    try {
      const response = await axios.delete(
        `${apiUrl}/activity/activitydelete/${id}`
      );

      if (response.data.success) {
        const deleted = activities.find((activity) => activity._id === id);
        setDeletedActivity(deleted);
        setActivities(activities.filter((activity) => activity._id !== id));
        setShowUndoModal(true);
        
        // Auto-close undo modal after 5 seconds
        setTimeout(() => {
          setShowUndoModal(false);
          setDeletedActivity(null);
        }, 5000);
      } else {
        toast.error(
          response.data.message || "Failed to delete activity",
          "error"
        );
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(
        error.response?.data?.message || "Error deleting activity",
        "error"
      );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Enhanced Header */}
        <div className="mb-8 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/10 via-purple-500/10 to-pink-500/10 blur-3xl"></div>
          <div className="relative">
            <div className="flex items-center gap-3 mb-3">
              <div className="p-3 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl shadow-lg">
                <Sparkles className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-5xl font-black bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 bg-clip-text text-transparent">
                Activity Management
              </h1>
            </div>
            <p className="text-gray-600 text-lg ml-16">
              Manage your activities, update details, and track bookings with ease
            </p>
          </div>
        </div>

        {/* Activity Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {loading ? (
            <div className="col-span-full flex items-center justify-center py-20">
              <Loader2 className="w-12 h-12 text-blue-500 animate-spin" />
              <span className="ml-3 text-gray-600 text-lg">
                Loading activities...
              </span>
            </div>
          ) : activities.length > 0 ? (
            activities.map((activity) => (
              <ActivityCard
                key={activity._id}
                activity={activity}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-20">
              <p className="text-gray-500 text-xl">No activities found</p>
              <p className="text-gray-400 mt-2">
                Add your first activity to get started!
              </p>
            </div>
          )}
        </div>
      </div>
      <style jsx>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fade-in 0.2s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ActivityUpdate;